/**
 * c-appointo-ux.js
 * 予約ウィジェット（Appointo）の入力しやすさを補う。
 *
 * Appointo 側で設定できないものだけを扱う：
 *   1. 入力欄のプレースホルダ（アプリに設定項目が無い）
 *   2. 時間スロット一覧の「まだ下に続く」表示（c-appointo.css の .is-scrollable を付け外し）
 *   3. 電話番号の国番号の初期値を日本（+81）にする（海外の国番号も選べるまま。アプリ設定は初期化で US に戻ることがあるため）
 *   4. プルダウンの未選択表示「Choose an option」の日本語化（翻訳画面に項目が無い）
 *   5. カレンダー ⇄ 入力フォームの切替時、ウィジェット上端が固定ヘッダーに隠れないよう戻す
 *   6. 時間スロットの残り数を隠し、予約が入った時間帯だけ「△」を出す（アプリに表示切替が無い）
 *   7. 受付期間外の月を開いた時の文言を「受付開始前」に差し替える（アプリの文言は満席の時と共通のため）
 *
 * ウィジェットは開くたびに DOM を作り直すため、MutationObserver で追従する。
 * （時間を1つ押すだけでも、Appointo は時間スロットを全部描き直す）
 * 表示の書き換え（1・3・4・6）は、画面に描かれる前にその場で済ませる。
 * 待ってから書き換えると、書き換え前の表示が一瞬だけ見えてしまう。
 * 見た目の調整は c-appointo.css 側が担当し、ここでは状態の付与だけを行う。
 */
(function () {
  'use strict';

  var SLOTS_SELECTOR = '.appointo-calendar-slots';
  var SCROLLABLE_CLASS = 'is-scrollable';
  var SCROLL_EPSILON = 8; // 最下部判定の余裕（px）
  var REFRESH_DEBOUNCE_MS = 120; // 高さ・位置を読む処理は、再描画が落ち着いてからまとめて1回だけ行う
  var REWRITE_BUDGET = 20; // 上の待ち時間1回の間に、その場で書き込める回数の上限（下の observer を参照）

  /**
   * 入力欄のプレースホルダ。
   * キーは «その欄を一意に指せるセレクタ» で、Appointo のフォームは
   * メール / 氏名（textarea） / 電話（number）の3つで構成される。
   */
  var PLACEHOLDERS = [
    { selector: 'input[type="email"].appointo-input', text: 'cene@example.com' },
    // カスタム質問の id は数値で採番されるため、項目名（name 属性）で見分ける。
    // 次の氏名用より先に置く（既に入っている欄は上書きしないため、順序が効く）
    { selector: 'textarea[name*="フリガナ"]', text: '例）ヤマダ タロウ' },
    { selector: 'textarea[name*="お取り置き"]', text: '例）https://cene.tokyo/products/…' },
    { selector: 'textarea.appointo-textarea', text: '例）山田 太郎' },
    { selector: 'input[type="number"].appointo-input', text: '例）09012345678' }
  ];

  function applyPlaceholders(root) {
    PLACEHOLDERS.forEach(function (item) {
      var nodes = root.querySelectorAll(item.selector);
      for (var i = 0; i < nodes.length; i++) {
        // すでに何か入っていれば尊重する
        if (!nodes[i].placeholder) nodes[i].placeholder = item.text;
      }
    });
  }

  /**
   * 電話番号の国番号の初期値を日本にする。
   * 既定が US (+1) のままだと日本の番号が入力チェックで弾かれるため、
   * フォームが作られた直後に一度だけ JP を選ぶ。海外のお客様（インバウンド）も
   * 予約できるよう、他国の選択肢は残し、お客様が選び直した値は上書きしない。
   */
  var PHONE_COUNTRY_SELECTOR = 'select.appointo-phone-number';
  var PHONE_COUNTRY_JP = '81';

  function defaultPhoneCountry(root) {
    var selects = root.querySelectorAll(PHONE_COUNTRY_SELECTOR);
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      if (select.dataset.ceneCountryDefaulted) continue;
      select.dataset.ceneCountryDefaulted = '1';

      var hasJp = false;
      for (var j = 0; j < select.options.length; j++) {
        if (select.options[j].value === PHONE_COUNTRY_JP) hasJp = true;
      }
      // アプリ側の選択肢から JP が消えた場合・すでに JP の場合は触らない
      if (!hasJp || select.value === PHONE_COUNTRY_JP) continue;

      select.value = PHONE_COUNTRY_JP;
      // Appointo が change で内部状態を持っている場合に備えて通知する
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * 電話番号の欄は type="number" で描画されるため、PC では入力中にマウスのホイールを
   * 回すと数字が増減してしまう（例: 09012345678 → 9012345677。先頭の 0 も消える）。
   * 入力中（フォーカスがある時）だけホイールの既定動作を止める。
   * フォーカスが無い時はそのままページがスクロールする。右端の矢印は c-appointo.css で消している。
   */
  var NUMBER_INPUT_SELECTOR = 'input[type="number"].appointo-input';

  function stopWheelOnFocusedNumber(e) {
    if (document.activeElement === this) e.preventDefault();
  }

  function guardNumberInputs(root) {
    var inputs = root.querySelectorAll(NUMBER_INPUT_SELECTOR);
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].dataset.ceneWheelGuarded) continue;
      inputs[i].dataset.ceneWheelGuarded = '1';
      // passive: false を明示しないと preventDefault が効かないブラウザがある
      inputs[i].addEventListener('wheel', stopWheelOnFocusedNumber, { passive: false });
    }
  }

  /**
   * プルダウン（カスタム質問）の未選択表示を日本語にする。
   * Appointo は値も「Choose an option」のまま入力チェックに使っているため、
   * 値は変えず表示テキストだけを差し替える。
   */
  var SELECT_PROMPT_VALUE = 'Choose an option';
  var SELECT_PROMPT_TEXT = '選択してください';

  function localizeSelectPrompt(root) {
    var wrote = false;
    var options = root.querySelectorAll('select option');
    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      if (option.value !== SELECT_PROMPT_VALUE || option.text === SELECT_PROMPT_TEXT) continue;
      // この option は value 属性を持たず、表示テキストがそのまま値になる。
      // 先に value を固定しないと、表示を変えた時点で値も変わり必須チェックを通ってしまう
      option.setAttribute('value', SELECT_PROMPT_VALUE);
      option.text = SELECT_PROMPT_TEXT;
      wrote = true;
    }
    return wrote;
  }

  /**
   * 「次へ」「戻る」で画面が切り替わると、ページのスクロール位置はそのままで
   * 中身の高さだけが変わるため、ウィジェットの上端が固定ヘッダーの裏に入る。
   * 切り替わりを検知したら、上端が隠れているときだけヘッダーの下まで戻す。
   * （テーマは固定ヘッダーの高さを html の scroll-padding-top に入れているので
   *  scrollIntoView はヘッダー分を自動で空けてくれる）
   */
  var lastStep = null;

  function currentStep(root) {
    var email = root.querySelector('input[type="email"].appointo-input');
    return email && email.offsetParent !== null ? 'form' : 'calendar';
  }

  function keepWidgetBelowHeader(root) {
    var step = currentStep(root);
    if (lastStep !== null && step !== lastStep) {
      var headerBottom = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      if (root.getBoundingClientRect().top < headerBottom) {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    lastStep = step;
  }

  /** スロット一覧が「まだ下にスクロールできる」かどうかを class で表す */
  function updateScrollState(el) {
    var remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    el.classList.toggle(SCROLLABLE_CLASS, remaining > SCROLL_EPSILON);
  }

  function bindSlots(root) {
    var lists = root.querySelectorAll(SLOTS_SELECTOR);
    for (var i = 0; i < lists.length; i++) {
      var el = lists[i];

      if (!el.dataset.ceneScrollBound) {
        el.dataset.ceneScrollBound = '1';
        el.addEventListener('scroll', function () {
          updateScrollState(this);
        });
      }

      // 高さはスロットが描画されてから確定するため、バインド済みでも毎回見直す。
      // （初回だけの評価では scrollHeight が 0 のままで判定を誤る）
      updateScrollState(el);
    }
  }

  /**
   * 時間スロットの残り数（「4/5 残りの空き時間」）を出さず、
   * 1件でも予約が入った時間帯だけ「△」にする（CENE 指定：平日・土日共通）。
   * 満席の時間帯は Appointo 側が選べなくするので、ここでは扱わない。
   * 書き換えは MutationObserver を再び動かすため、表示が変わる時だけ書く。
   */
  var REMAINING_SELECTOR = '.appointo-remaining-slot';
  var REMAINING_PATTERN = /(\d+)\s*\/\s*(\d+)/;
  var FEW_LEFT_MARK = '△';
  var FEW_LEFT_CLASS = 'is-few-left';

  function markRemaining(root) {
    var wrote = false;
    var nodes = root.querySelectorAll(REMAINING_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var match = REMAINING_PATTERN.exec(el.textContent);
      // 一度書き換えた要素は数値が消えているので、控えておいた判定を使う
      var fewLeft = match ? Number(match[1]) < Number(match[2]) : el.dataset.ceneFewLeft === '1';
      if (match) el.dataset.ceneFewLeft = fewLeft ? '1' : '0';

      var text = fewLeft ? FEW_LEFT_MARK : '';
      if (el.textContent !== text) {
        el.textContent = text;
        wrote = true;
      }
      el.classList.toggle(FEW_LEFT_CLASS, fewLeft);
    }
    return wrote;
  }

  /**
   * 受付期間の外（予約枠がまだ解放されていない月）を開いた時の文言を差し替える（CENE 指定・10/19）。
   * Appointo は「その日に枠が無い」時に1つの文言（翻訳の chooseDate）しか持たないため、
   * アプリの翻訳を変えると、受付中の月で当日の枠が埋まった時も「受付開始前」と出てしまう。
   * そこでテーマ側で、次の3つが揃った時だけ文言を差し替える:
   *   - その月に予約できる日が1日も無い（Appointo が #apt-no-availability の案内を出す）
   *   - 表示中の月が今月より後（今月の残りが満席なだけの時は、元の文言のまま）
   *   - 案内の「次の空き日」（例「Next Available Slot : 11月, 30」）が表示中の月より前
   *     ＝受付期間の終わりを過ぎている。次の空き日が後ろなら、受付中の月が満席なだけなので元の文言のまま
   *     （次の空き日が読めない時は、上の2つだけで判断する）
   */
  var NO_SLOTS_SELECTOR = '.appointo-no-slots';
  var NO_MONTH_AVAILABILITY_SELECTOR = '#apt-no-availability';
  var NEXT_AVAILABLE_MONTH_PATTERN = /(\d{1,2})\s*月/;
  var NOT_YET_OPEN_TEXT =
    '受付開始前の日程です。予約枠の解放までお待ちいただくか、現在受付中の日程をご予約ください。';

  /** 表示中の月を「年×12＋月」で返す。読めなければ null */
  function shownMonthIndex(root) {
    // 日付マスの data-calendar-date は「Tue Dec 01 2026 …」形式（受付外の日にも付いている）。
    // 前月・翌月のマスが端に混ざっても誤らないよう、真ん中のマスで月を見る
    var days = root.querySelectorAll('.vanilla-calendar-date[data-calendar-date]');
    if (!days.length) return null;
    var date = new Date(days[Math.floor(days.length / 2)].getAttribute('data-calendar-date'));
    if (isNaN(date.getTime())) return null;
    return date.getFullYear() * 12 + date.getMonth();
  }

  /** 案内の「次の空き日」が表示中の月より前なら true・後なら false・読めなければ null */
  function nextAvailableIsBefore(banner, shown, current) {
    var match = NEXT_AVAILABLE_MONTH_PATTERN.exec(banner.textContent);
    if (!match) return null;
    // 案内には年が無い。次の空き日は今日より前にはならないので、
    // 今月から数えて最初に来るその月（今月〜11か月先）とみなす
    var ahead = (((Number(match[1]) - 1 - (current % 12)) % 12) + 12) % 12; // 0〜11
    return current + ahead < shown;
  }

  function relabelNotYetOpen(root) {
    var el = root.querySelector(NO_SLOTS_SELECTOR);
    if (!el || el.textContent === NOT_YET_OPEN_TEXT) return false;
    var banner = root.querySelector(NO_MONTH_AVAILABILITY_SELECTOR);
    if (!banner) return false;

    var shown = shownMonthIndex(root);
    var now = new Date();
    var current = now.getFullYear() * 12 + now.getMonth();
    if (shown === null || shown <= current) return false;
    if (nextAvailableIsBefore(banner, shown, current) === false) return false;

    el.textContent = NOT_YET_OPEN_TEXT;
    return true;
  }

  function getModal() {
    return document.getElementById('appointo-modal-content');
  }

  /**
   * Appointo が描いた表示を書き換える処理。レイアウト（高さ・位置）は読まない。
   * 同じ表示に対して2回目以降は何も書き込まないこと（子要素・テキストへの書き込みは
   * MutationObserver をもう一度動かすため、毎回書くと止まらなくなる）。
   * 戻り値は、その「MutationObserver を動かす書き込み」をしたかどうか。
   */
  function rewrite(modal) {
    applyPlaceholders(modal);
    defaultPhoneCountry(modal);
    var wrote = localizeSelectPrompt(modal);
    wrote = relabelNotYetOpen(modal) || wrote;
    return markRemaining(modal) || wrote;
  }

  function refresh() {
    var modal = getModal();
    if (!modal) return;
    rewrite(modal);
    bindSlots(modal);
    guardNumberInputs(modal);
    keepWidgetBelowHeader(modal);
  }

  // ウィジェットは開閉・画面遷移のたびに作り直されるので DOM の変化を監視する。
  var refreshTimer = null;
  var rewriteCount = 0;
  var observer = new MutationObserver(function () {
    // 表示の書き換えは待たずにその場で行う。MutationObserver はブラウザが画面を
    // 描く前に呼ばれるので、書き換え前の表示（「5/5 残りの空き時間」・国番号 US・
    // 「Choose an option」）が画面に出ない。
    // 上限は保険：通常は Appointo の描き直し1回につき書き込みも1回で収まる。
    // 万一アプリ側がこの書き込みに反応して描き直すようになっても、書き換えの応酬で
    // ページが固まらないよう、上限を超えたら下の待ち処理（従来の動き）に任せる。
    var modal = getModal();
    if (modal && rewriteCount < REWRITE_BUDGET && rewrite(modal)) rewriteCount++;

    // 高さ・位置を読む処理は、変更が大量に連続する再描画が落ち着いてから1回だけ。
    if (refreshTimer) return;
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      rewriteCount = 0;
      refresh();
    }, REFRESH_DEBOUNCE_MS);
  });

  function start() {
    refresh();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
